'use client'
import { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import usePrint from '../hooks/usePrint'
import Modal from '@crystal-eyes/components/elements/Modal'
import styles from './PrintModal.module.scss'
import usePersonality from '@crystal-eyes/hooks/profiles/usePersonality'
import usePersonalInfo from '@crystal-eyes/hooks/profiles/usePersonalInfo'
import Icon, { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import printImage from '@dashboard/public/static/share/images/save-as-pdf.png'
import Checkbox from '@crystal-eyes/components/elements/Input/Checkbox'

interface Identity {
  id: string
}

type Props = { identity?: Identity }

const CONTENT_SELECTOR = '#content'

export default function PrintModal({ identity }: Props) {
  const {
    modalState: { show: showModal, setShow: setShowModal },
    printState: { state: printState, setState: setPrintState },
  } = usePrint()
  const { data: personalInfo } = usePersonalInfo({ identity })
  const { data: personality } = usePersonality({ identity })
  const [headings, setHeadings] = useState<HTMLElement[]>([])

  useEffect(() => {
    const setPrintSections = setTimeout(() => {
      const content = document.querySelector(CONTENT_SELECTOR)
      const headings: HTMLElement[] = Array.from(
        content?.querySelectorAll('h2:not([data-no-print="true"])') || [],
      )

      setHeadings(headings)
    }, 2000)

    return () => {
      clearTimeout(setPrintSections)
    }
  }, [])

  useEffect(() => {
    if (
      headings.length > 0 &&
      (!printState || Object.keys(printState).length === 0)
    ) {
      const startingPrintState = {}
      headings.forEach(heading => {
        startingPrintState[heading.id] = true
      })

      setPrintState(startingPrintState)
    }
  }, [printState, headings.length])

  if (!showModal) return null

  return (
    <Modal
      className={styles.printModal}
      close={() => {
        setShowModal(false)
      }}
    >
      <div
        className={`CE disc-background-light disc-color ${personality?.discType} heading`}
      >
        <Icon icon={Icons.Download} className={styles.icon} />

        <h2 className="CE Parented disc-color">
          Download {personalInfo?.firstName}'s Personality
        </h2>

        <Link
          href="#"
          className="close-link"
          onClick={evt => {
            evt.preventDefault()
            setShowModal(false)
          }}
        >
          X
        </Link>
      </div>

      <div className="content">
        <div className="selection">
          <p className="description">
            Select the sections you would like to download:
          </p>

          <div className="section-options">
            {headings.map(heading => (
              <div key={heading.id}>
                <Checkbox
                  id={`print_${heading.id}`}
                  className="print-checkbox"
                  value={heading.id}
                  checked={printState && printState[heading.id]}
                  onChange={evt => {
                    setPrintState({
                      ...printState,
                      [evt.target.value]: !printState![evt.target.value],
                    })
                  }}
                />

                <label htmlFor={`#print_${heading.id}`}>
                  {heading.dataset['sidebarTitle'] || heading.textContent}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="explanation">
          <p>
            To save your document as a PDF, select <br /> <em>“Save as PDF”</em>{' '}
            in your print window.
          </p>

          <Image
            src={printImage.src}
            height={240}
            width={300}
            alt="Print screen example"
          />
        </div>
      </div>

      <div className="download-cta">
        <Link
          href="#"
          onClick={evt => {
            evt.preventDefault()
            window.print()
          }}
          className="CE button-link action-button"
        >
          Download
        </Link>
      </div>
    </Modal>
  )
}
